import useSWR, { type SWRConfiguration } from 'swr';
import denApi from '@/lib/api/denApi';
import {
  ContractAbiDetails,
  ContractAbiDetailsErrorCode,
  ContractAbiDetailsType,
  NetworkType,
} from '@mls-wallet/shared-types';

type ContractAbiParams = {
  address: string;
  networkType: NetworkType;
};

/**
 * Fetcher for contract ABI details.
 *
 * This endpoint returns a discriminated union:
 * - `type: CONTRACT`: regular smart contract ABI details
 * - `type: ERC-20`: ERC-20 ABI details + token metadata (name/symbol/decimals)
 */
const fetcher = async (
  params: ContractAbiParams
): Promise<ContractAbiDetails> => {
  return await denApi.get<ContractAbiDetails, ContractAbiParams>(
    'contract/abi-details',
    params
  );
};

/**
 * React hook for fetching contract ABI details for a specific address and network.
 *
 * Returns:
 * - `contractName`: the backend contract name (implementation name for proxies)
 * - `contractDisplayName`: UX-friendly display label; for ERC-20 prefers token metadata
 * - `tokenMetadata`: present only when `type === ContractAbiDetailsType.ERC20`
 * - `proxyFunctions` / `implementationFunctions`: write functions grouped by proxy vs implementation ABIs
 *
 * NOTE: This hook does not do runtime validation of the response; it assumes the backend matches
 * `ContractAbiDetails` from `@mls-wallet/shared-types`.
 */
export function useContractAbi(
  params: ContractAbiParams | null,
  swrOptions?: SWRConfiguration<ContractAbiDetails>
) {
  const swrKey = params
    ? `contract/abi-details/${params.address}/${params.networkType}`
    : null;

  const { data, error, isLoading, mutate } = useSWR<ContractAbiDetails>(
    swrKey,
    params ? () => fetcher(params) : null,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
      ...(swrOptions ?? {}),
    }
  );

  /**
   * Token metadata is only available when the backend identified this address as an ERC-20 token.
   */
  const tokenMetadata =
    data?.type === ContractAbiDetailsType.ERC20 ? data.tokenMetadata : null;

  const errorDetails =
    data?.type === ContractAbiDetailsType.ERROR
      ? {
          errorCode: data.errorCode,
          message: data.message,
        }
      : null;

  // For policy contract selector UX: prefer ERC-20 token identity over raw contract name.
  // Primary goal is to show something like "USDC" instead of "FiatTokenProxy".
  const contractDisplayName =
    data?.type === ContractAbiDetailsType.ERC20
      ? (tokenMetadata?.symbol ??
        tokenMetadata?.name ??
        data?.contractName ??
        null)
      : data && data.type !== ContractAbiDetailsType.ERROR
        ? data.contractName
        : null;

  return {
    type: data?.type ?? null,
    contractName:
      data && data.type !== ContractAbiDetailsType.ERROR
        ? data.contractName
        : null,
    contractDisplayName,
    tokenMetadata,
    proxyFunctions:
      data && data.type !== ContractAbiDetailsType.ERROR
        ? data.proxyFunctions
        : [],
    implementationFunctions:
      data && data.type !== ContractAbiDetailsType.ERROR
        ? data.implementationFunctions
        : [],
    errorCode: errorDetails?.errorCode ?? null,
    errorMessage: errorDetails?.message ?? null,
    // Handy for call sites that want to branch on error type
    isUnverifiedContract:
      errorDetails?.errorCode ===
      ContractAbiDetailsErrorCode.UNVERIFIED_CONTRACT,
    isLoading,
    error,
    refetch: mutate,
  };
}
